import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import cls from 'classnames';

import { l10n } from '@ott/l10n';

import TimerIcon from './blocks/TimerIcon';

import styles from './Timer.scss';

const Timer = ({
  className,
  showTimeLabel,
  showTimeIcon,
  showEmptyHours,
  to,
  deadlineHandler,
  childrenClassNames: { dayClass, hourClass, minuteClass, secondsClass },
}) => {
  let timer = null;

  const [date, setDate] = useState({
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
  });

  const calculateTime = () => {
    let time = to - new Date();
    if (time < 0) {
      time = 0;
    }

    setDate({
      day: Math.floor(time / (1000 * 60 * 60 * 24)),
      hour: Math.floor((time / (1000 * 60 * 60)) % 24),
      minute: Math.floor((time / 1000 / 60) % 60),
      second: Math.floor((time / 1000) % 60),
    });

    if (time <= 0) {
      clearTimeout(timer);
      if (deadlineHandler) {
        deadlineHandler();
      }

      return;
    }

    timer = setTimeout(() => calculateTime(), 1000);
  };

  useEffect(() => {
    calculateTime();

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const formatDateNumber = (number, length) => {
    if ((typeof number === 'number' || typeof +number === 'number') && length > 0) {
      const numberString = number.toString();
      if (numberString.length <= length) {
        return new Array(length - numberString.length).fill(0).concat(numberString).join('');
      }
    }

    return '';
  };

  const renderDayNode = () => {
    if (!date.day) {
      return null;
    }

    const text = showTimeLabel ? l10n('timer.days', { days: +date.day }) : `${+date.day}:`;

    return <span className={cls(styles.days, dayClass)}>{text}</span>;
  };

  const renderHourNode = () => {
    if (!date.hour && !showEmptyHours) {
      return null;
    }

    const hoursText = formatDateNumber(date.hour, 2);
    const text = showTimeLabel
      ? date.hour > 0 || showEmptyHours
        ? l10n('timer.hours', { hours: hoursText })
        : ''
      : `${hoursText}:`;

    return date.hour > 0 || showEmptyHours ? (
      <span className={cls(styles.item, hourClass)}>{text}</span>
    ) : null;
  };

  const renderMinuteNode = () => {
    const minuteText = formatDateNumber(+date.minute, 2);
    const text = showTimeLabel ? l10n('timer.minutes', { minutes: minuteText }) : `${minuteText}:`;

    return <span className={cls(styles.minutes, minuteClass)}>{text}</span>;
  };

  const renderSecondsNode = () => {
    const secondsText = formatDateNumber(+date.second, 2);
    const text = showTimeLabel ? l10n('timer.seconds', { seconds: secondsText }) : secondsText;

    return <span className={cls(styles.seconds, secondsClass)}>{text}</span>;
  };

  return (
    <span className={cls(styles.timer, className)}>
      {showTimeIcon ? (
        <span className={styles.timeIcon}>
          <TimerIcon />
        </span>
      ) : null}
      {renderDayNode()}
      {renderHourNode()}
      {renderMinuteNode()}
      {renderSecondsNode()}
    </span>
  );
};

Timer.propTypes = {
  className: PropTypes.string,
  to: PropTypes.instanceOf(Date),
  showTimeLabel: PropTypes.bool,
  showEmptyHours: PropTypes.bool,
  showTimeIcon: PropTypes.bool,
  deadlineHandler: PropTypes.func,
  childrenClassNames: PropTypes.shape({
    dayClass: PropTypes.string,
    hourClass: PropTypes.string,
    minuteClass: PropTypes.string,
    secondsClass: PropTypes.string,
  }),
};
Timer.defaultProps = {
  showTimeLabel: false,
  showTimeIcon: true,
  showEmptyHours: true,
  deadlineHandler: () => {},
  childrenClassNames: {
    dayClass: '',
    hourClass: '',
    minuteClass: '',
    secondsClass: '',
  },
};

export default Timer;
